/**
 * Padrón General de Ministros Report
 * All ministers (active + inactive) with status color coding and credential flag.
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedCard } from "@/components/ui/animated-card";
import { AnimatedNumber } from "@/components/ui/animated-number";
import { DataColumnHeader } from "@/components/molecules/table";
import {
  SelfContainedDataTable,
  selectedIncludesFn,
} from "@/components/organisms/SelfContainedDataTable";
import { InteractivePieChartCard } from "@/components/organisms/InteractivePieChartCard";
import { useUserProfile } from "@/features/ministers/hooks/useUserProfile";
import { useMinisterRegistryReport } from "../hooks/useMinisterRegistryReport";
import {
  getInactiveReasonBadgeVariant,
  getMinisterRowStyle,
  translateInactiveReason,
} from "../utils/ministerColors";
import { ExportDropdown } from "../components/ExportDropdown";
import type { ExportColumn } from "../utils/exportExcel";
import type { MinisterRegistryEntry } from "../types";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

const columns: ColumnDef<MinisterRegistryEntry>[] = [
  {
    accessorKey: "adb_code",
    header: ({ column }) => <DataColumnHeader column={column} title="Código ADB" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => (
      <span className="font-mono text-xs">{row.original.adb_code ?? "—"}</span>
    ),
  },
  {
    accessorKey: "full_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Nombre" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => <span className="font-medium">{row.original.full_name}</span>,
  },
  {
    accessorKey: "dni",
    header: ({ column }) => <DataColumnHeader column={column} title="DNI" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.dni ?? "—",
  },
  {
    accessorKey: "degree_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Grado" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.degree_name ?? "—",
  },
  {
    accessorKey: "district_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Distrito" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.district_name ?? "—",
  },
  {
    accessorKey: "region_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Región" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.region_name ?? "—",
  },
  {
    id: "status_label",
    accessorFn: (m) =>
      m.is_active ? "Activo" : (m.inactive_reason_label ?? translateInactiveReason(m.inactive_reason_code) ?? "Inactivo"),
    header: ({ column }) => <DataColumnHeader column={column} title="Estado" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => {
      const m = row.original;
      if (m.is_active) return <Badge variant="success" className="text-[10px]">Activo</Badge>;
      const label = m.inactive_reason_label ?? translateInactiveReason(m.inactive_reason_code);
      return <Badge variant={getInactiveReasonBadgeVariant(m.inactive_reason_code)} className="text-[10px]">{label}</Badge>;
    },
  },
  {
    id: "credential",
    accessorFn: (m) =>
      m.is_active ? (m.missing_from_credentials ? "Sin credencial" : "Con credencial") : "—",
    header: ({ column }) => <DataColumnHeader column={column} title="Credencial" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => {
      const m = row.original;
      if (!m.is_active) return <span className="text-muted-foreground">—</span>;
      return m.missing_from_credentials
        ? <Badge variant="default" className="text-[10px]">Sin credencial</Badge>
        : <Badge variant="outline" className="text-[10px] text-success border-success/40">Con credencial</Badge>;
    },
  },
  {
    accessorKey: "inactive_at",
    header: ({ column }) => <DataColumnHeader column={column} title="Fecha inact." />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) =>
      row.original.inactive_at
        ? new Date(row.original.inactive_at).toLocaleDateString("es-BO")
        : "—",
  },
  {
    accessorKey: "latest_status_note",
    header: "Nota",
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => (
      <span
        className="block max-w-[200px] truncate text-muted-foreground"
        title={row.original.latest_status_note ?? undefined}
      >
        {row.original.latest_status_note ?? "—"}
      </span>
    ),
  },
];

const EXPORT_COLUMNS: ExportColumn<MinisterRegistryEntry>[] = [
  { label: "Código ADB",        getValue: (m) => m.adb_code ?? "" },
  { label: "Nombre",            getValue: (m) => m.full_name },
  { label: "DNI",               getValue: (m) => m.dni ?? "" },
  { label: "Grado",             getValue: (m) => m.degree_name ?? "" },
  { label: "Distrito",          getValue: (m) => m.district_name ?? "" },
  { label: "Región",            getValue: (m) => m.region_name ?? "" },
  { label: "Estado",            getValue: (m) => m.is_active ? "Activo" : (m.inactive_reason_label ?? translateInactiveReason(m.inactive_reason_code) ?? "Inactivo") },
  { label: "Credencial",        getValue: (m) => m.is_active ? (m.missing_from_credentials ? "Sin credencial" : "Con credencial") : "—" },
  { label: "Fecha inactivación",getValue: (m) => m.inactive_at ? new Date(m.inactive_at).toLocaleDateString("es-BO") : "" },
  { label: "Nota",              getValue: (m) => m.latest_status_note ?? "" },
];

export function MinisterRegistryReportPage() {
  const navigate = useNavigate();
  const { isAdmin } = useUserProfile();

  const { entries, filters, summary, isLoading, updateFilter, resetFilters } =
    useMinisterRegistryReport();

  const [filteredRows, setFilteredRows] = useState<MinisterRegistryEntry[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);

  const handleFilteredDataChange = useCallback(
    (rows: MinisterRegistryEntry[], filtered: boolean) => {
      setFilteredRows(rows);
      setIsFiltered(filtered);
    },
    []
  );

  const REASON_COLORS: Record<string, string> = {
    retired:              "#94a3b8",
    passive:              "#f59e0b",
    abandon:              "#f97316",
    deceased:             "#6b7280",
    observed:             "#8b5cf6",
    discharged:           "#f43f5e",
    dismissed:            "#ef4444",
    disciplinary_process: "#3b82f6",
  };

  const inactiveReasonPieData = Array.from(summary.byReason.entries()).map(
    ([code, count]) => ({
      label: translateInactiveReason(code),
      value: count,
      color: REASON_COLORS[code],
    })
  );

  const filterPanel = (
    <div className="flex flex-wrap items-end gap-3 p-3">
      <div className="w-[100px]">
        <label className="mb-1 block text-xs text-muted-foreground">Año (credencial)</label>
        <Select value={String(filters.year)} onValueChange={(v) => updateFilter("year", parseInt(v))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {YEAR_OPTIONS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" size="sm" onClick={resetFilters}>Limpiar</Button>
    </div>
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate("/reports")} className="mb-4">
          <ArrowLeft className="mr-2 size-4" />Volver a Reportes
        </Button>
        <h1 className="text-3xl font-bold">Padrón General de Ministros</h1>
        <p className="mt-2 text-muted-foreground">
          Todos los ministros activos e inactivos con estado de credencial
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryCard label="Total" value={summary.total} isLoading={isLoading} />
        <SummaryCard label="Activos" value={summary.active} isLoading={isLoading} valueClassName="text-success" />
        <SummaryCard label="Inactivos" value={summary.inactive} isLoading={isLoading} valueClassName="text-muted-foreground" />
        <SummaryCard label="Sin credencial" value={summary.missingCredentials} isLoading={isLoading} valueClassName="text-primary" />
      </div>

      {/* Inactive reasons chart */}
      {!isLoading && inactiveReasonPieData.length > 0 && (
        <InteractivePieChartCard
          title="Inactivos por Razón"
          data={inactiveReasonPieData}
          seriesName="Ministros"
          tableHeaders={{ label: "Razón", value: "Ministros", percentage: "%" }}
          layout="wide-table"
          chartHeight="280px"
        />
      )}

      {/* Table */}
      <SelfContainedDataTable
        data={entries}
        columns={columns}
        isLoading={isLoading}
        getSearchableText={(m) => {
          const adbCode = m.adb_code ?? "";
          return `${adbCode} ${adbCode.replace(/\D/g, "")} ${m.full_name ?? ""} ${m.dni ?? ""}`;
        }}
        searchPlaceholder="Buscar por nombre, DNI o código ADB..."
        initialSorting={[{ id: "full_name", desc: false }]}
        filterPanel={filterPanel}
        onRowClick={isAdmin ? (m) => navigate(`/ministers/${m.id}`) : undefined}
        getRowClassName={isAdmin ? () => "cursor-pointer" : undefined}
        getRowStyle={(m) => getMinisterRowStyle(m.is_active, m.inactive_reason_code, m.missing_from_credentials)}
        emptyMessage="No se encontraron ministros"
        defaultPageSize={50}
        pageSizeOptions={[20, 50, 100]}
        onFilteredDataChange={handleFilteredDataChange}
        toolbarActions={
          <ExportDropdown
            rows={filteredRows}
            isFiltered={isFiltered}
            filename={`padron-general-${filters.year}`}
            columns={EXPORT_COLUMNS}
            getDistrictName={(m) => m.district_name ?? null}
            getRegionName={(m) => m.region_name ?? null}
            getSortName={(m) => m.full_name ?? ""}
            disabled={isLoading}
          />
        }
      />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  isLoading,
  valueClassName,
}: {
  label: string;
  value: number;
  isLoading: boolean;
  valueClassName?: string;
}) {
  return (
    <AnimatedCard variant="gradient" className="border-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-semibold text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-8 w-16 animate-pulse rounded bg-muted" />
        ) : (
          <AnimatedNumber value={value} className={`text-2xl font-bold md:text-3xl ${valueClassName ?? ""}`} />
        )}
      </CardContent>
    </AnimatedCard>
  );
}

